import { useState, useEffect, useRef } from 'react';
import LazyImage from '../../../components/LazyImage';

const slides = [
  {
    title: 'Start, manage, and grow your business with confidence.',
    subtitle: 'From company formation and compliance to tax, legal, and corporate structuring, we\'re here to guide you every step of the way.',
    cta: 'Discover More',
    ctaLink: '#services',
    feature: 'Build, launch, and scale successfully',
  },
  {
    title: 'Expert Compliance Solutions for Regulated Industries',
    subtitle: 'Navigate complex regulatory requirements with confidence. Our team ensures your business stays compliant and competitive.',
    cta: 'Learn More',
    ctaLink: '#services',
    feature: 'Risk & Compliance Excellence',
  },
  {
    title: 'Your Trusted Partner in Cyprus and Beyond',
    subtitle: 'Tailored support that simplifies your journey into new markets with expert guidance and local expertise.',
    cta: 'Get Started',
    ctaLink: '#contact',
    feature: 'Your Growth Partner',
  },
];

const heroImages = [
  'https://public.readdy.ai/ai/img_res/68577edf7cd7aaa5df0cff6532f14f21.jpg',
  'https://readdy.ai/api/search-image?query=Dramatic%20modern%20architecture%20with%20sweeping%20curved%20metallic%20surfaces%20reflecting%20sunset%20golden%20hour%20light%20abstract%20geometric%20building%20facade%20deep%20blue%20and%20amber%20tones%20futuristic%20corporate%20aesthetic&width=1920&height=1080&seq=hero2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Mediterranean%20coastal%20city%20at%20sunset%20with%20modern%20business%20district%20and%20historic%20harbor%20golden%20light%20reflecting%20on%20calm%20azure%20sea%20Cyprus%20Malta%20style%20professional%20travel%20aesthetic&width=1920&height=1080&seq=hero3&orientation=landscape',
];

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([false, false, false]);
  const [slide1Ready, setSlide1Ready] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setIsAnimating(false);
      }, 300);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const goToSlide = (index: number) => {
    if (index !== currentSlide) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsAnimating(false);
      }, 300);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleImageLoad = (index: number) => {
    setImagesLoaded(prev => {
      const newState = [...prev];
      newState[index] = true;
      return newState;
    });
    if (index === 0) setSlide1Ready(true);
  };

  return (
    <section ref={heroRef} id="hero" className="relative h-screen w-full overflow-hidden rounded-b-3xl">
      {/* Background Images — slide 1 eager+high priority, slides 2+3 deferred until slide 1 ready */}
      {heroImages.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-700 ${
            currentSlide === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Only render img tag for slide 1 immediately; slides 2+3 wait until slide 1 loaded */}
          {(index === 0 || slide1Ready) && (
            <img
              src={image}
              alt={`Hero background ${index + 1}`}
              width={1920}
              height={1080}
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding={index === 0 ? 'sync' : 'async'}
              fetchPriority={index === 0 ? 'high' : 'low'}
              onLoad={() => handleImageLoad(index)}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent"></div>
        </div>
      ))}

      {/* Slide Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="w-full lg:w-1/2">
            <div
              className={`transition-all duration-500 ${
                isAnimating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
              }`}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6">
                {slides[currentSlide].title}
              </h1>
              <div className="flex items-start gap-4 mb-8">
                <div className="w-0.5 h-20 bg-white/50 flex-shrink-0"></div>
                <p className="text-lg text-white/80 leading-relaxed">
                  {slides[currentSlide].subtitle}
                </p>
              </div>
              <button
                onClick={() => scrollToSection(slides[currentSlide].ctaLink)}
                className="px-6 py-3 bg-transparent border-2 border-amber-500 text-white font-semibold rounded-lg hover:bg-amber-500 transition-all duration-300 whitespace-nowrap"
              >
                {slides[currentSlide].cta}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators - Left Side */}
      <div className="absolute left-8 top-1/2 -translate-y-1/2 z-20 hidden md:flex flex-col gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 transition-all duration-300 rounded-full cursor-pointer ${
              currentSlide === index
                ? 'h-5 bg-white'
                : 'h-2 bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Feature Box at Bottom */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 hidden lg:block">
        <div className="bg-black/25 backdrop-blur-sm rounded-t-xl border-l border-r border-t border-white/25">
          <div className="flex items-center h-28 px-8">
            {slides.map((slide, index) => (
              <div key={index} className="flex items-center">
                <button
                  onClick={() => goToSlide(index)}
                  className="flex-1 flex items-center justify-center cursor-pointer px-6"
                >
                  <div className="w-60 text-left relative">
                    {currentSlide === index && (
                      <div className="absolute -top-7 left-0 w-12 h-0.5 bg-white"></div>
                    )}
                    <span
                      className={`text-xl font-medium text-white transition-opacity ${
                        currentSlide === index ? 'opacity-100' : 'opacity-50'
                      }`}
                    >
                      {slide.feature}
                    </span>
                  </div>
                </button>
                {index < slides.length - 1 && (
                  <div className="w-px h-28 bg-white/25 mx-4"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
